import { AuthBrand } from "@/features/auth/components/layout";
import { usePreferencesPageCopy } from "@/features/i18n/preferencesPageCopy";
import { usePublicPreferencesQuery, usePublicUpdatePreferences } from "@/features/preferences/api/usePublicPreferences";
import { Label } from "@patient-watch/ui/label";
import { Switch } from "@patient-watch/ui/switch";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Loader2 } from "lucide-react";

export const Route = createFileRoute("/preferences/$userId")({
    component: PreferencesPage,
});

function PreferencesCard({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex flex-col justify-center items-center gap-6 mx-auto px-2 py-12 min-h-screen max-w-lg">
            <AuthBrand />
            <div className="flex flex-col gap-6 bg-white shadow-lg p-8 rounded-xl w-full max-w-2xl">{children}</div>
        </div>
    );
}

function PreferencesPage() {
    const { userId } = Route.useParams();
    const copy = usePreferencesPageCopy();
    const { data: preferences, isLoading, isError } = usePublicPreferencesQuery(userId);
    const updatePreferences = usePublicUpdatePreferences();

    if (isLoading) {
        return (
            <PreferencesCard>
                <div className="flex flex-col justify-center items-center gap-4 py-8">
                    <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                    <p className="text-slate-500 text-sm">{copy.loading}</p>
                </div>
            </PreferencesCard>
        );
    }

    if (isError || !preferences) {
        return (
            <PreferencesCard>
                <div className="flex flex-col justify-center items-center gap-4 py-8 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                    <div>
                        <h1 className="font-semibold text-slate-900 text-xl">{copy.notFoundTitle}</h1>
                        <p className="mt-1 text-slate-500 text-sm">{copy.notFoundDescription}</p>
                    </div>
                </div>
            </PreferencesCard>
        );
    }

    return (
        <PreferencesCard>
            <div>
                <h1 className="font-semibold text-slate-900 text-xl">{copy.title}</h1>
                <p className="mt-1 text-slate-500 text-sm">{copy.description}</p>
            </div>

            <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center gap-4">
                    <Label htmlFor="preferences-email">{copy.emailLabel}</Label>
                    <Switch
                        id="preferences-email"
                        checked={preferences.email}
                        disabled={updatePreferences.isPending}
                        onCheckedChange={(checked) =>
                            updatePreferences.mutate({ user_id: userId, email: checked, sms: preferences.sms })
                        }
                    />
                </div>
                <div className="flex justify-between items-center gap-4">
                    <Label htmlFor="preferences-sms">{copy.smsLabel}</Label>
                    <Switch
                        id="preferences-sms"
                        checked={preferences.sms}
                        disabled={updatePreferences.isPending}
                        onCheckedChange={(checked) =>
                            updatePreferences.mutate({ user_id: userId, email: preferences.email, sms: checked })
                        }
                    />
                </div>
            </div>
        </PreferencesCard>
    );
}
